import { useState } from 'react'
import { initializePaddle } from '@paddle/paddle-js'
import { Check, X, Zap, Layers, Shield } from 'lucide-react'
import { planDefinitions, type PlanName } from '../plans'
import { useAuthStore } from '../store'
import { useToastStore } from '../store/toast'

function configured(value?: string) {
  return Boolean(value && !value.startsWith('replace-with') && !value.startsWith('your-') && !value.includes('<'))
}

interface UpgradeModalProps {
  isOpen: boolean
  onClose: () => void
  currentPlan?: 'free' | 'pro' | 'advanced'
}

export default function UpgradeModal({ isOpen, onClose, currentPlan = 'free' }: UpgradeModalProps) {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')
  const [checkoutPlan, setCheckoutPlan] = useState<string | null>(null)
  const user = useAuthStore((state) => state.user)
  const addToast = useToastStore((state) => state.addToast)

  if (!isOpen) return null

  const paddleEnvironment = import.meta.env.VITE_PADDLE_ENVIRONMENT === 'production' ? 'production' : 'sandbox'
  const paddleClientToken = import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string | undefined
  const priceIds = {
    Pro: {
      monthly: import.meta.env.VITE_PADDLE_PRO_MONTHLY_PRICE_ID as string | undefined,
      yearly: import.meta.env.VITE_PADDLE_PRO_YEARLY_PRICE_ID as string | undefined,
    },
    Advanced: {
      monthly: import.meta.env.VITE_PADDLE_ADVANCED_MONTHLY_PRICE_ID as string | undefined,
      yearly: import.meta.env.VITE_PADDLE_ADVANCED_YEARLY_PRICE_ID as string | undefined,
    },
  }
  const hasYearlyPrices = configured(priceIds.Pro.yearly) && configured(priceIds.Advanced.yearly)
  const activeBillingCycle = hasYearlyPrices ? billingCycle : 'monthly'

  const openCheckout = async (planName: Exclude<PlanName, 'Free'>) => {
    const priceId = priceIds[planName][activeBillingCycle]
    if (!configured(paddleClientToken) || !configured(priceId)) {
      addToast({
        type: 'warning',
        message: 'Paddle is not configured yet. Add the client token and price IDs to the production environment.',
      })
      return
    }
    const token = paddleClientToken as string
    const selectedPriceId = priceId as string

    try {
      setCheckoutPlan(planName)
      const paddle = await initializePaddle({
        environment: paddleEnvironment,
        token,
        debug: true,
        pwCustomer: user?.email ? { email: user.email } : undefined,
        eventCallback: (event) => {
          if (event.name === 'checkout.error' || event.type === 'checkout.error') {
            console.error('Paddle checkout error', event)
            addToast({
              type: 'error',
              message: event.detail || event.code || 'Paddle checkout failed.',
            })
          }
          if (event.type === 'checkout.warning') {
            console.warn('Paddle checkout warning', event)
          }
        },
      })
      paddle?.Checkout.open({
        items: [{ priceId: selectedPriceId, quantity: 1 }],
        customer: user?.email ? { email: user.email } : undefined,
        customData: user?.id ? { user_id: user.id, plan: planName.toLowerCase(), billing_cycle: activeBillingCycle } : undefined,
        settings: {
          displayMode: 'overlay',
          theme: 'light',
          successUrl: `${window.location.origin}/settings?checkout=success`,
        },
      })
    } catch (error) {
      console.error('Paddle checkout failed:', error)
      addToast({ type: 'error', message: 'Could not open checkout. Please try again.' })
    } finally {
      setCheckoutPlan(null)
    }
  }

  const plans = planDefinitions.map((plan) => ({
    ...plan,
    cta: plan.name === 'Free'
      ? 'Current Plan'
      : `${plan.currency}${plan.price[activeBillingCycle]}/${activeBillingCycle === 'monthly' ? 'month' : 'year'}`,
    disabled:
      plan.name === 'Free' ||
      (plan.name === 'Pro' && (currentPlan === 'pro' || currentPlan === 'advanced')) ||
      (plan.name === 'Advanced' && currentPlan === 'advanced'),
  }))

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" 
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative inline-block w-full max-w-6xl px-4 py-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">
              Choose Your Plan
            </h2>
            <p className="mt-2 text-gray-600">
              Start free, upgrade when you need more power
            </p>

            {hasYearlyPrices ? (
              <div className="mt-4 flex items-center justify-center gap-3">
                <span className={`text-sm ${billingCycle === 'monthly' ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
                  Monthly
                </span>
                <button
                  onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    billingCycle === 'yearly' ? 'bg-brand-blue' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      billingCycle === 'yearly' ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className={`text-sm ${billingCycle === 'yearly' ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
                  Yearly <span className="text-green-600 text-xs">(Save 15%)</span>
                </span>
              </div>
            ) : (
              <p className="mt-4 text-sm text-gray-500">Monthly plans are available now. Yearly billing can be added later.</p>
            )}
          </div>

          {/* Plans grid */}
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`relative rounded-2xl border-2 p-6 transition-all ${
                  plan.highlight
                    ? 'border-brand-blue shadow-lg bg-brand-light/30'
                    : 'border-gray-200 bg-white'
                } ${plan.disabled ? 'opacity-70' : ''}`}
              >
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-brand-blue text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
                  <div className="mt-4">
                    {plan.price[activeBillingCycle] > 0 ? (
                      <>
                        <span className="text-4xl font-bold text-gray-900">
                          {plan.currency}{plan.price[activeBillingCycle]}
                        </span>
                        <span className="text-gray-500">
                          /{activeBillingCycle === 'monthly' ? 'mo' : 'yr'}
                        </span>
                      </>
                    ) : (
                      <span className="text-4xl font-bold text-gray-900">Free</span>
                    )}
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
                      )}
                      <span
                        className={`text-sm ${
                          feature.included ? 'text-gray-700' : 'text-gray-400'
                        }`}
                      >
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  disabled={plan.disabled}
                  onClick={() => {
                    if (plan.name === 'Pro' || plan.name === 'Advanced') {
                      openCheckout(plan.name)
                    }
                  }}
                  className={`w-full py-3 px-4 rounded-xl font-semibold transition-colors ${
                    plan.highlight
                      ? 'bg-brand-blue text-white hover:bg-brand-accent'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } ${plan.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {checkoutPlan === plan.name ? 'Opening checkout...' : plan.cta}
                </button>
              </div>
            ))}
          </div>

          {/* Features highlight */}
          <div className="mt-10 grid md:grid-cols-3 gap-6 pt-8 border-t border-gray-200">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto bg-purple-100 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mt-3">Advanced OCR</h4>
              <p className="text-sm text-gray-600 mt-1">
                State-of-the-art OCR for complex layouts, handwriting, and multilingual documents
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto bg-blue-100 rounded-xl flex items-center justify-center">
                <Layers className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mt-3">Split-Screen Viewer</h4>
              <p className="text-sm text-gray-600 mt-1">
                View PDF and extracted data side-by-side with synchronized scrolling
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto bg-green-100 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mt-3">Enterprise Security</h4>
              <p className="text-sm text-gray-600 mt-1">
                Full encryption rollout by 2027 with SOC 2 compliance
              </p>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  )
}
